import API_BASE_URL, { IS_TAURI } from '../config';
import './oidcTokenExchange';

export interface TauriHttpFetchRequest {
  url: string;
  method: string;
  headers: [string, string][];
  bodyBase64: string | null;
}

export interface TauriHttpFetchResponse {
  status: number;
  statusText: string;
  headers: [string, string][];
  bodyBase64: string;
}

export interface TauriFetchBridgeOptions {
  apiBaseUrl?: string;
  isTauri?: boolean;
}

const NULL_BODY_STATUSES = new Set([101, 204, 205, 304]);

let originalWindowFetch: typeof window.fetch | null = null;

function createAbortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError');
}

export function encodeUtf8(text: string): Uint8Array {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(text);
  }
  const encoded = unescape(encodeURIComponent(text));
  const bytes = new Uint8Array(encoded.length);
  for (let i = 0; i < encoded.length; i += 1) {
    bytes[i] = encoded.charCodeAt(i);
  }
  return bytes;
}

export function decodeUtf8(bytes: Uint8Array): string {
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder().decode(bytes);
  }
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return decodeURIComponent(escape(binary));
}

export function bytesToBase64(bytes: Uint8Array): string {
  if (bytes.byteLength === 0) {
    return '';
  }
  const chunkSize = 0x8000;
  let binary = '';
  for (let offset = 0; offset < bytes.byteLength; offset += chunkSize) {
    const slice = bytes.subarray(offset, Math.min(offset + chunkSize, bytes.byteLength));
    binary += String.fromCharCode.apply(null, Array.from(slice));
  }
  return btoa(binary);
}

export function base64ToBytes(base64: string): Uint8Array {
  if (!base64) {
    return new Uint8Array(0);
  }
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function getEffectiveApiBaseUrl(override?: string): string {
  if (override !== undefined) {
    return override.replace(/\/+$/, '');
  }
  const runtime =
    typeof window !== 'undefined' ? window.__ENV__?.REACT_APP_API_BASE_URL : undefined;
  return (runtime || API_BASE_URL || '').replace(/\/+$/, '');
}

export function resolveTauriApiUrl(rawUrl: string, apiBaseUrl: string): string | null {
  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return null;
  }

  const normalizedBase = apiBaseUrl.replace(/\/+$/, '');

  // Relative /api/... path
  if (trimmed === '/api' || trimmed.startsWith('/api/') || trimmed.startsWith('/api?')) {
    if (normalizedBase) {
      return `${normalizedBase}${trimmed}`;
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}${trimmed}`;
    }
    return null;
  }

  // Absolute URL targeting API_BASE_URL directly
  if (
    normalizedBase &&
    (trimmed === normalizedBase ||
      trimmed.startsWith(`${normalizedBase}/`) ||
      trimmed.startsWith(`${normalizedBase}?`))
  ) {
    return trimmed;
  }

  // Tauri shell origin URLs (http://tauri.localhost/api/..., https://tauri.localhost/api/..., tauri://localhost/api/...)
  try {
    const parsed = new URL(trimmed);
    const isTauriShellHost =
      parsed.hostname === 'tauri.localhost' || parsed.protocol === 'tauri:';
    if (
      isTauriShellHost &&
      (parsed.pathname === '/api' || parsed.pathname.startsWith('/api/'))
    ) {
      const suffix = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      if (normalizedBase) {
        return `${normalizedBase}${suffix}`;
      }
      return trimmed;
    }
  } catch {
    // Not a valid absolute URL
  }

  return null;
}

function extractHeaders(
  input: RequestInfo | URL,
  init?: RequestInit
): [string, string][] {
  const merged = new Headers(
    typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined
  );
  if (init?.headers) {
    const initHeaders = new Headers(init.headers);
    initHeaders.forEach((value, key) => {
      merged.set(key, value);
    });
  }
  const pairs: [string, string][] = [];
  merged.forEach((value, key) => {
    pairs.push([key, value]);
  });
  return pairs;
}

function hasHeader(headers: [string, string][], name: string): boolean {
  const lower = name.toLowerCase();
  return headers.some(([key]) => key.toLowerCase() === lower);
}

async function extractBodyAndHeaders(
  finalUrl: string,
  method: string,
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  headers: [string, string][]
): Promise<{ bodyBase64: string | null; headers: [string, string][] }> {
  if (method === 'GET' || method === 'HEAD') {
    return { bodyBase64: null, headers };
  }

  const body = init?.body;
  if (body !== undefined && body !== null) {
    if (typeof body === 'string') {
      return { bodyBase64: bytesToBase64(encodeUtf8(body)), headers };
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      const nextHeaders = hasHeader(headers, 'content-type')
        ? headers
        : [...headers, ['content-type', 'application/x-www-form-urlencoded;charset=UTF-8'] as [string, string]];
      return { bodyBase64: bytesToBase64(encodeUtf8(body.toString())), headers: nextHeaders };
    }
    if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) {
      return { bodyBase64: bytesToBase64(new Uint8Array(body)), headers };
    }
    if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(body)) {
      const view = new Uint8Array(body.buffer, body.byteOffset, body.byteLength);
      return { bodyBase64: bytesToBase64(view), headers };
    }
    if (typeof Blob !== 'undefined' && body instanceof Blob && typeof body.arrayBuffer === 'function') {
      const nextHeaders =
        body.type && !hasHeader(headers, 'content-type')
          ? [...headers, ['content-type', body.type] as [string, string]]
          : headers;
      const buf = await body.arrayBuffer();
      return { bodyBase64: bytesToBase64(new Uint8Array(buf)), headers: nextHeaders };
    }
  }

  // Fallback for FormData, Blob (without direct arrayBuffer), or Request with body
  if (
    (body !== undefined && body !== null) ||
    (typeof Request !== 'undefined' && input instanceof Request && input.body !== null)
  ) {
    const req =
      typeof Request !== 'undefined' && input instanceof Request && !init
        ? input.clone()
        : new Request(finalUrl, {
            method,
            headers,
            body: body ?? undefined,
          });
    const nextHeaders = [...headers];
    req.headers.forEach((value, key) => {
      if (!hasHeader(nextHeaders, key)) {
        nextHeaders.push([key, value]);
      }
    });
    const buf = await req.arrayBuffer();
    const bytes = new Uint8Array(buf);
    return {
      bodyBase64: bytes.byteLength > 0 ? bytesToBase64(bytes) : null,
      headers: nextHeaders,
    };
  }

  return { bodyBase64: null, headers };
}

function invokeWithAbort<T>(
  promiseFactory: () => Promise<T>,
  signal?: AbortSignal | null
): Promise<T> {
  if (signal?.aborted) {
    return Promise.reject(createAbortError());
  }
  if (!signal) {
    return promiseFactory();
  }

  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      reject(createAbortError());
    };
    signal.addEventListener('abort', onAbort, { once: true });

    promiseFactory().then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        if (signal.aborted) {
          reject(createAbortError());
          return;
        }
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', onAbort);
        if (signal.aborted) {
          reject(createAbortError());
          return;
        }
        reject(error);
      }
    );
  });
}

/**
 * In the Tauri desktop shell, WebView2 serves the frontend from
 * `http://tauri.localhost`. Direct browser `fetch()` calls with `Authorization`
 * or `X-Forwarded-Host` headers trigger CORS `OPTIONS` preflights from that
 * origin. Routing API calls through the Rust `tauri_http_fetch` IPC command
 * performs the HTTP request via `reqwest`, bypassing WebView2 CORS entirely.
 */
export function setupTauriFetchBridge(options?: TauriFetchBridgeOptions): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }

  const isTauri = options?.isTauri ?? IS_TAURI;
  const invoke = window.__TAURI_INTERNALS__?.invoke;
  if (!isTauri || typeof invoke !== 'function') {
    return;
  }

  if (!originalWindowFetch) {
    originalWindowFetch = window.fetch.bind(window);
  }
  const fallbackFetch = originalWindowFetch;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const currentInvoke = window.__TAURI_INTERNALS__?.invoke;
    if (typeof currentInvoke !== 'function') {
      return fallbackFetch(input, init);
    }

    const rawUrl =
      typeof input === 'string'
        ? input
        : input instanceof URL
        ? input.toString()
        : input.url;

    const apiBaseUrl = getEffectiveApiBaseUrl(options?.apiBaseUrl);
    const targetUrl = resolveTauriApiUrl(rawUrl, apiBaseUrl);
    if (!targetUrl) {
      return fallbackFetch(input, init);
    }

    const signal =
      init?.signal ??
      (typeof Request !== 'undefined' && input instanceof Request ? input.signal : undefined);

    if (signal?.aborted) {
      throw createAbortError();
    }

    const rawMethod =
      init?.method ??
      (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET');
    const method = (rawMethod || 'GET').toUpperCase();

    const initialHeaders = extractHeaders(input, init);
    const { bodyBase64, headers } = await extractBodyAndHeaders(
      targetUrl,
      method,
      input,
      init,
      initialHeaders
    );

    if (signal?.aborted) {
      throw createAbortError();
    }

    const responsePayload = await invokeWithAbort(
      () =>
        currentInvoke<TauriHttpFetchResponse>('tauri_http_fetch', {
          url: targetUrl,
          method,
          headers,
          bodyBase64,
        }),
      signal
    );

    const responseHeaders = new Headers();
    for (const [name, value] of responsePayload.headers || []) {
      try {
        responseHeaders.append(name, value);
      } catch {
        // Ignore invalid header names if any
      }
    }

    const isNullBody = NULL_BODY_STATUSES.has(responsePayload.status);
    if (isNullBody) {
      return new Response(null, {
        status: responsePayload.status,
        statusText: responsePayload.statusText,
        headers: responseHeaders,
      });
    }

    const decodedBytes = base64ToBytes(responsePayload.bodyBase64);
    const bodyBuffer = decodedBytes.buffer.slice(
      decodedBytes.byteOffset,
      decodedBytes.byteOffset + decodedBytes.byteLength
    ) as ArrayBuffer;

    return new Response(bodyBuffer, {
      status: responsePayload.status,
      statusText: responsePayload.statusText,
      headers: responseHeaders,
    });
  };
}

export function restoreTauriFetchBridge(): void {
  if (typeof window !== 'undefined' && originalWindowFetch) {
    window.fetch = originalWindowFetch;
    originalWindowFetch = null;
  }
}
